// Which notification kinds ride the account's outbound webhooks, and whether a
// (kind, channel) pair can deliver at all. In `domain` because two apps need it
// and apps never import apps: the worker's fan-out routes with it, and the API's
// settings read marks a webhook cell `available` with it.

import { NotificationChannel, type NotificationKind } from './notification';
import { OutboundEventType, type SubscribableOutboundEventType } from './webhook';

/**
 * Notification kind → slice-03 outbound event type, where one exists. Only the
 * kinds that map to a stable outbound contract can ride the account's webhooks;
 * every kind ABSENT from the map below has no outbound event, so the webhook
 * channel records it `no_target` (push + email carry those). Webhook is off by
 * default in the matrix anyway — this is opt-in.
 *
 * The absent set is stated as a PROPERTY rather than listed, because the list
 * that stood here was four kinds long and the enum had ten: `bug_scouted`,
 * `ops_digest`, `ask_duplicate` and `backlog_imported` all landed without it, and
 * `onboarding_wait` (#3766) would have been the fifth. The map is `Partial`, so
 * nothing makes a hand-kept complement fail — and a sentence nothing can falsify
 * is how a docblock comes to describe a shape the code left behind.
 *
 * The VALUE type is the subscribable set (`@developerz/domain`), not the full
 * outbound vocabulary: mapping a kind onto an event `POST /v1/webhooks` refuses
 * to store would fan out to zero webhooks forever, which reads as an endpoint
 * that went quiet. Adding a row here therefore means adding its event to
 * `SUBSCRIBABLE_OUTBOUND_EVENT_TYPES` first — that is the compile error, and it
 * is the same edit that lets a maintainer subscribe to it.
 */
export const KIND_TO_OUTBOUND: Partial<Record<NotificationKind, SubscribableOutboundEventType>> = {
  pr_merged: OutboundEventType.PrMerged,
  escalation: OutboundEventType.IssueEscalated,
  // What `escalation` carried before the kinds were narrowed: a subscriber to
  // `issue.escalated` keeps receiving every "a person is needed" event.
  decision_needed: OutboundEventType.IssueEscalated,
};

/**
 * Can `kind` be delivered on `channel` at all? Push and email carry every kind;
 * the webhook channel carries only the kinds with an outbound event. A pair that
 * is not available has nothing to switch, on any surface.
 */
export function isChannelAvailable(kind: NotificationKind, channel: NotificationChannel): boolean {
  return channel !== NotificationChannel.Webhook || kind in KIND_TO_OUTBOUND;
}
